<template>
  <div class="w-full">
    <div v-if="label.length > 0" class="flex">
      <label
        v-if="label.length > 0" class="text-xs tracking-wide uppercase label" :class="{ required }"
        v-text="label"
      />
    </div>
    <div class="flex">
      <tx-select
        v-model="season" style="width: 100px" :data="Object.values(indexedSeasons)" value-prop="value"
        display-prop="displayLabel" :disabled="disabled" :clearable="clearable" :errors="errors" @change="onSeasonSelect"
      />
      <tx-autocomplete
        ref="refArticleNumber" v-model="articleId" class="ml-1" :errors="errors"
        :fetch-suggestions="searchArticleNumber" value-prop="ArticleId" display-prop="ArticleNumber"
        :disabled="disabled || !utils.isValidStringValue(season)" :clearable="clearable" @change="onArticleNumberChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import type { ErrorObject } from '@vuelidate/core'
import TxSelect from '@/shared/components/TxSelect.vue'
import TxAutocomplete from '@/shared/components/TxAutocomplete.vue'
import utils from '@/services/utils'
import { useUserStore } from '@/store/userData'
import type { ArticleFilterModel } from '@/api/t1/model/articleModel'
import { FilterCriteriaMode } from '@/models/filterCriteria'
import { getArticleFilterValues } from '@/api/t1/article'

interface IProps {
  modelValue?: string | null
  label?: string
  required?: boolean
  disabled?: boolean
  clearable?: boolean
  errors?: ErrorObject[]
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: null,
  label: '',
  required: false,
  clearable: false,
  disabled: false,
  errors: () => [] as ErrorObject[],
})

const emit = defineEmits<{
  (e: 'update:modelValue', val: string | null): void
  (e: 'change', val?: string, rawVal?: any): void
}>()

const { t } = useI18n()
const userStore = useUserStore()

const refArticleNumber = ref<InstanceType<typeof TxAutocomplete>>()
const season = ref<string | null>(null)
const articleId = ref<number | null>(null)

const indexedSeasons = computed<Record<string, { key: string, value: string, catalogCode: number, displayLabel: string }>>(() => {
  if (userStore.activeCatalog && userStore.linkedCatalogDetails && Object.keys(userStore.linkedCatalogDetails).length) {
    const catalogCode = userStore.activeCatalog.CatalogCode
    const indexedSeasonValueCount = Object.values(userStore.linkedCatalogDetails).reduce((acu, linkedCatalog) => {
      if (linkedCatalog.Status && linkedCatalog.CatalogCode !== catalogCode) {
        const seasonValue = utils.isValidStringValue(linkedCatalog.Season) ? linkedCatalog.Season : t('general.blankValue')
        if (!acu[seasonValue]) {
          acu[seasonValue] = 0
        }
        acu[seasonValue]++
      }
      return acu
    }, {})

    return Object.values(userStore.linkedCatalogDetails).reduce((acu, linkedCatalog) => {
      if (linkedCatalog.Status && linkedCatalog.CatalogCode !== catalogCode) {
        const seasonValue = utils.isValidStringValue(linkedCatalog.Season) ? linkedCatalog.Season : t('general.blankValue')
        const seasonKey = `${seasonValue}${linkedCatalog.CatalogCode}`
        if (!acu[seasonKey]) {
          acu[seasonKey] = {
            key: seasonKey,
            value: seasonKey,
            catalogCode: linkedCatalog.CatalogCode,
            displayLabel: indexedSeasonValueCount[seasonValue] <= 1 ? seasonValue : `${seasonValue} (${linkedCatalog.CatalogCode})`,
          }
        }
      }
      return acu
    }, {})
  }
  return {}
})

function notifyUpdate(val: string, rawVal?: any) {
  emit('update:modelValue', val)
  emit('change', val, rawVal)
}

function onSeasonSelect() {
  articleId.value = null
  notifyUpdate('')
}

function onArticleNumberChange(val?: any, rawVal?: any) {
  if (rawVal) {
    notifyUpdate(`${season.value}//${rawVal.ArticleId}//${rawVal.ArticleNumber}`, rawVal)
  }
  else {
    notifyUpdate('')
  }
}

function searchArticleNumber(query: string, callback) {
  if (utils.isDefined(season.value) && utils.isDefined(indexedSeasons.value[season.value])) {
    const catalogCode = indexedSeasons.value[season.value].catalogCode
    const filters: ArticleFilterModel[] = []
    if (utils.isDefined(query)) {
      filters.push({ attribute: 'ArticleNumber', mode: FilterCriteriaMode.startWithString, value: query.trim(), exclude: false })
    }

    getArticleFilterValues(catalogCode, { attributes: ['ArticleId', 'ArticleNumber'], filters, sortCriteria: { attribute: 'ArticleNumber', isAsc: true } }, 100)
      .then((data) => {
        callback(data)
      })
  }
  else {
    callback([])
  }
}

function setArticleNumberValue(articleNumber: string) {
  if (utils.isDefined(refArticleNumber.value)) {
    refArticleNumber.value.setDisplayModelValue(articleNumber)
  }
}

onMounted(() => {
  if (utils.isDefined(props.modelValue) && utils.isValidStringValue(props.modelValue)) {
    const splits = props.modelValue.split('//')
    if (utils.isDefined(indexedSeasons.value[splits[0]])) {
      season.value = indexedSeasons.value[splits[0]].value
      articleId.value = Number(splits[1])
      setArticleNumberValue(splits[2])
    }
  }
})
</script>
